import React from 'react';
import DoughnutGauge from '@/areas/dashboard/components/DoughnutGauge';
import DoughnutChart from '@/areas/dashboard/components/DoughnutChart';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useApiPortalV1AssessmentsResultsListQuery } from '@/areas/assessment-schedule/api';
import styled from 'styled-components';
import { Divider } from 'semantic-ui-react';
import { PillarData } from '@/areas/culture-hub/types/api';
import { MobilePageSection, BodyText } from '@/mobile/layouts/common/MobilePageStyles';

import { AggregateType } from '@/common/constants/AggregateType';
interface ScoreGaugeCardProps {
  overallScore: number;
  differential: number | null;
  previousScore: number | null;
  aiSummary?: { summary?: string; bullets?: string[] } | null;
  assessmentPeriodId?: number;
  teamId?: number;
  teamName?: string | null;
  employeeId?: number;
  pillars?: PillarData[];
}

interface SentimentScore {
  id: number;
  label: string;
  caption: string;
  value: number;
  color: string;
}

const VERY_POSITIVE = 0;
const SOMEWHAT_POSITIVE = 1;
const NEUTRAL = 2;
const SOMEWHAT_NEGATIVE = 3;
const VERY_NEGATIVE = 4;

const MobileScoreGaugeCard: React.FC<ScoreGaugeCardProps> = ({
  overallScore,
  differential,
  previousScore,
  aiSummary,
  assessmentPeriodId,
  teamId,
  teamName,
  employeeId,
  pillars: pillarsProp,
}) => {
  const getSummaryText = (): string | null => aiSummary?.summary || null;

  const summaryText = getSummaryText();
  const hasPreviousPeriod = previousScore !== null && differential !== null;
  const displayedDiff = hasPreviousPeriod ? (Number(overallScore.toFixed(1)) - Number((previousScore as number).toFixed(1))) : null;
  const sourcePillars = React.useMemo(() => (Array.isArray(pillarsProp) ? pillarsProp : []), [pillarsProp]);

  const topThemesByPillar = React.useMemo(() => {
    if (!Array.isArray(sourcePillars) || sourcePillars.length === 0) return [];

    return sourcePillars
      .map((p) => {
        const themes = Array.isArray(p.themes) ? p.themes : [];
        if (themes.length === 0) return null;
        let best = themes[0];
        let bestScore = Math.max(0, Math.min(1, Number((best as any).sentiment ?? 0)));
        for (let i = 1; i < themes.length; i += 1) {
          const t = themes[i];
          const s = Math.max(0, Math.min(1, Number((t as any).sentiment ?? 0)));
          if (s > bestScore) {
            best = t;
            bestScore = s;
          }
        }
        return { pillarKey: p.key, pillarName: p.name, theme: best, strength: bestScore };
      })
      .filter(Boolean)
      .slice(0, 6);
  }, [sourcePillars]);

  const { data: sentimentData } = useApiPortalV1AssessmentsResultsListQuery(
    { aggregateType: AggregateType.TEAM_SUMMARY, assessmentPeriodId: assessmentPeriodId!, employeeId },
    { skip: !assessmentPeriodId }
  );

  const [sentimentScores, setSentimentScores] = React.useState<SentimentScore[]>([]);

  React.useEffect(() => {
    if (sentimentData && sentimentData.length > 0) {
      const result = sentimentData[0]?.result || {};
      let distribution = result?.org_distribution?.distribution;

      if (typeof teamId !== 'undefined' && Array.isArray(result?.team_distributions)) {
        const found = result.team_distributions.find((td: any) => Number(td.team_id) === Number(teamId));
        if (found && Array.isArray(found.distribution)) {
          distribution = found.distribution;
        }
      }

      const scores: SentimentScore[] = [];
      if (Array.isArray(distribution) && distribution.length > 0) {
        const map = distribution.reduce<Record<string, number>>((acc, cur) => {
          acc[cur.bucket] = Number(cur.percentage) || 0;
          return acc;
        }, {} as Record<string, number>);

        scores.push({ id: VERY_NEGATIVE, label: 'Very Negative', caption: 'Dissatisfied and disengaged', value: map['Very Negative'] ?? 0, color: 'rgba(255, 50, 90, .8)' });
        scores.push({ id: SOMEWHAT_NEGATIVE, label: 'Somewhat Negative', caption: 'Concerned, wants improvement', value: map['Negative'] ?? 0, color: 'rgba(255, 50, 90, .4)' });
        scores.push({ id: NEUTRAL, label: 'Neutral', caption: 'Mixed or indifferent feelings', value: map['Neutral'] ?? 0, color: 'rgba(255, 216, 111, 1)' });
        scores.push({ id: SOMEWHAT_POSITIVE, label: 'Somewhat Positive', caption: 'Satisfied, room to grow', value: map['Positive'] ?? 0, color: 'rgba(129, 199, 132, .5)' });
        scores.push({ id: VERY_POSITIVE, label: 'Very Positive', caption: 'Thriving and highly engaged', value: map['Very Positive'] ?? 0, color: 'rgba(129, 199, 132, 1)' });
      }

      setSentimentScores(scores);
    } else {
      setSentimentScores([]);
    }
  }, [sentimentData, teamId]);

  return (
    <CardShell>
      <HeaderRow>
        <GaugeWrapper>
          <DoughnutGauge max={10} complete={overallScore} precision={1} size="medium" />
          {hasPreviousPeriod && displayedDiff !== null && (
            <TrendBadge>
              {displayedDiff > 0 ? (
                <TrendingUp size={16} />
              ) : displayedDiff < 0 ? (
                <TrendingDown size={16} />
              ) : (
                <Minus size={16} />
              )}
              <BadgeText>{displayedDiff > 0 ? `+${displayedDiff.toFixed(1)}` : displayedDiff.toFixed(1)}</BadgeText>
            </TrendBadge>
          )}
        </GaugeWrapper>

        <MetricColumn>
          <MetricItem>
            <MetricLabel>Current Score</MetricLabel>
            <MetricValue diff={displayedDiff}>{overallScore.toFixed(1)}</MetricValue>
          </MetricItem>
          <MetricItem>
            <MetricLabel>Previous Score</MetricLabel>
            <MetricValue>{hasPreviousPeriod ? (previousScore as number).toFixed(1) : 'N/A'}</MetricValue>
          </MetricItem>
        </MetricColumn>
      </HeaderRow>

      {summaryText && (
        <>
          <Divider />
          <MobilePageSection padbottom='0px'>
            <SectionTitle>{(teamName ?? 'Team') + ' Overview'}</SectionTitle>
            <BodyText>{aiSummary?.summary}</BodyText>
          </MobilePageSection>
        </>
      )}

      {sentimentScores && sentimentScores.length > 0 && (
        <>
          <Divider />
          <MobilePageSection>
            <SectionTitle>Feedback Themes</SectionTitle>
            <ThemeContent>
              <ChartBlock>
                <DoughnutChart chartData={sentimentScores} size="medium" />
              </ChartBlock>
              <ThemeList>
                {sentimentScores.map((item, idx) => (
                  <ThemeListItem key={idx}>
                    <LegendBox color={item.color} />
                    <ThemeLabel>{item.label}</ThemeLabel>
                  </ThemeListItem>
                ))}
              </ThemeList>
            </ThemeContent>
            {topThemesByPillar.length > 0 && (
              <BodyText title={topThemesByPillar.map(e => '• ' + ((e as any).theme as any).label).join('\n')}>
                {topThemesByPillar.map(e => ((e as any).theme as any).label).join(' • ')}
              </BodyText>
            )}
          </MobilePageSection>
        </>
      )}
    </CardShell>
  );
};

const CardShell = styled.div`
  width: 100%;
  padding: 0px 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;


const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const GaugeWrapper = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 140px;
  min-width: 140px;
  max-width: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
`;

const TrendBadge = styled.div`
  position: absolute;
  bottom: -12px;
  left: 75%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 9999px;
  background: #ecfdf5;
  color: #166534;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
`;

const BadgeText = styled.span`
  display: inline-block;
  margin-left: 4px;
`;

const MetricColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: grid;
  gap: 10px;
  width: 100%;
  align-items: center;
  text-align: center;
`;

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  letter-spacing: 0.02em;
  font-family: 'Poppins', sans-serif;
`;

const MetricValue = styled.div<{ diff?: number | null }>`
  font-size: 16px;
  line-height: 1.2;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  color: ${({ diff }) =>
    diff == null
      ? '#475569'
      : diff > 0
        ? '#166534'
        : diff < 0
          ? '#b91c1c'
          : '#111827'};
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  font-family: 'Poppins', sans-serif;
`;

const ThemeContent = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 20px;
  width: 100%;
  align-items: flex-start;
  overflow-x: auto;
  padding-bottom: 8px;
`;

const ChartBlock = styled.div`
  flex: 0 0 auto;
  width: 120px;
  min-width: 120px;
  display: flex;
  justify-content: center;
`;

const ThemeList = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: grid;
  gap: 4px;
`;

const ThemeListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
`;

const ThemeLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
  color: #374151;
  line-height: 1.4;
`;

const LegendBox = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  background-color: ${props => props.color};
  border-radius: 3px;
`;

export default MobileScoreGaugeCard;



